import { Select as SelectPrimitive } from "@kobalte/core/select";
import { Check, ChevronDown, Plus } from "@fleet/icons";
import { type JSX, Show, splitProps } from "solid-js";
import { cn } from "@boveda/shared";

import { overlayPortalHost } from "./overlay-portal";

/**
 * Reference Select for the canon (Bóveda). Single-value picker built on
 * `@kobalte/core/select`, styled with the repo's design tokens (bg-popover/card,
 * border, ring, accent) so it is fully theme-safe and consistent with
 * `popover.tsx` / `dialog.tsx` / `checkbox.tsx` / `tooltip.tsx`.
 *
 * Features inherited from Kobalte: full keyboard nav, typeahead, ARIA listbox
 * semantics, portal-rendered animated panel. The trigger looks like a text
 * input; the selected item shows a check mark.
 */

export interface SelectOption<T> {
  value: T;
  label: string;
  disabled?: boolean;
}

/**
 * CTA rendered inside the dropdown when the Select has no options (AWS-style:
 * an empty combobox offers "Create X" / "Go to X" instead of a dead control).
 *
 * Pass `onSelect` to run an action (open a creation dialog) or `href` to render
 * a link (navigate to the screen that provides the options). Provide exactly one.
 * `can` (default `true`) gates the CTA on permission — when `false` the empty
 * dropdown falls back to a plain "no options" message and no creation affordance
 * is shown. Omit `emptyAction` entirely when the source is not creatable.
 */
export interface SelectEmptyAction {
  label: string;
  onSelect?: () => void;
  href?: string;
  /** Permission gate. When false, the CTA is hidden. Defaults to true.*/
  can?: boolean;
  /** Leading icon for the CTA. Defaults to a plus glyph.*/
  icon?: JSX.Element;
  /** Optional helper line shown above the CTA (e.g. "No lists yet").*/
  description?: string;
}

interface SelectProps<T> {
  options: SelectOption<T>[];
  value: T | undefined;
  onChange: (value: T) => void;
  placeholder?: string;
  /** Accessible label for the trigger when there is no visible <label>.*/
  "aria-label"?: string;
  disabled?: boolean;
  class?: string;
  /** Trigger width override; defaults to full width.*/
  triggerClass?: string;
  contentClass?: string;
  /** Optional leading adornment rendered inside the trigger (e.g. an icon).*/
  icon?: JSX.Element;
  /**
   * Shown inside the dropdown when `options` is empty. Lets the user jump to the
   * place that creates options instead of facing a dead Select. See
   * {@link SelectEmptyAction}.
   */
  emptyAction?: SelectEmptyAction;
  /** Message shown in the empty dropdown when there is no (permitted) CTA.*/
  emptyLabel?: string;
}

export function Select<T>(props: SelectProps<T>) {
  const [local] = splitProps(props, [
    "options",
    "value",
    "onChange",
    "placeholder",
    "aria-label",
    "disabled",
    "class",
    "triggerClass",
    "contentClass",
    "icon",
    "emptyAction",
    "emptyLabel",
  ]);

  const selectedOption = () => local.options.find((o) => o.value === local.value);
  const isEmpty = () => local.options.length === 0;

  return (
    <SelectPrimitive<SelectOption<T>>
      class={local.class}
      options={local.options}
      value={selectedOption()}
      // Single-select semantics: re-clicking the already-selected option must NOT
      // toggle it off. Kobalte's default lets a click on the current selection
      // emit `null` (deselect) — which flickers the field empty and, in forms that
      // read the Kobalte state directly, can silently clear the value before
      // submit (the transversal stock-adjustment#2 symptom). This is a required
      // single-select: the value starts empty (placeholder) and can always be
      // CHANGED to another option; it just can't be un-set by re-clicking.
      disallowEmptySelection
      onChange={(option) => {
        // With `disallowEmptySelection` Kobalte no longer emits null on a
        // re-click; keep the null-guard as a defensive floor.
        if (option) {
          local.onChange(option.value);
        }
      }}
      optionValue="label"
      optionTextValue="label"
      optionDisabled="disabled"
      disabled={local.disabled}
      placeholder={local.placeholder}
      itemComponent={(itemProps) => (
        <SelectPrimitive.Item
          item={itemProps.item}
          class={cn(
            "relative flex cursor-pointer select-none items-center justify-between gap-2 rounded-md px-2.5 py-2 text-sm outline-none",
            "text-popover-foreground transition-colors",
            "data-[highlighted]:bg-accent/10 data-[highlighted]:text-foreground",
            "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
            "data-[selected]:font-medium",
          )}
        >
          <SelectPrimitive.ItemLabel class="truncate">
            {itemProps.item.rawValue.label}
          </SelectPrimitive.ItemLabel>
          <SelectPrimitive.ItemIndicator class="flex shrink-0 items-center text-accent">
            <Check class="h-4 w-4" />
          </SelectPrimitive.ItemIndicator>
        </SelectPrimitive.Item>
      )}
    >
      <SelectPrimitive.Trigger
        aria-label={local["aria-label"]}
        class={cn(
          "flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm",
          "outline-none transition-colors",
          "hover:border-accent/50",
          "focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
          "data-[expanded]:ring-2 data-[expanded]:ring-inset data-[expanded]:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
          local.triggerClass,
        )}
      >
        <span class="flex min-w-0 items-center gap-2">
          <Show when={local.icon}>{local.icon}</Show>
          <SelectPrimitive.Value<SelectOption<T>> class="block truncate text-left">
            {(state) => state.selectedOption()?.label}
          </SelectPrimitive.Value>
        </span>
        <SelectPrimitive.Icon class="shrink-0 text-muted-foreground transition-transform duration-200 data-[expanded]:rotate-180">
          <ChevronDown class="h-4 w-4" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      {/* mount: host tagged data-live-announcer so modal Dialog
          Dialog modal NO oculte el listbox (CC-21) — ver overlay-portal.ts. */}
      <SelectPrimitive.Portal mount={overlayPortalHost()}>
        <SelectPrimitive.Content
          class={cn(
            // z-[220]: ABOVE the Dialog (overlay z-[200], content z-[210]) —
            // defensive (CC-21): with z-50 a Select opened inside a Dialog
            // would be BELOW the modal and its options would be unclickable.
            "z-[220] min-w-[var(--kb-popper-anchor-width)] overflow-hidden rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md outline-none",
            // Opacity-only entrance (.ui-select-content in global.css): DO NOT animate
            // transform here — it would clash with @floating-ui's positioning
            // transform and the panel would flicker/jump when opened.
            "ui-select-content",
            local.contentClass,
          )}
        >
          <Show
            when={!isEmpty()}
            fallback={<SelectEmptyContent action={local.emptyAction} label={local.emptyLabel} />}
          >
            <SelectPrimitive.Listbox class="max-h-60 overflow-y-auto outline-none [scrollbar-gutter:stable]" />
          </Show>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive>
  );
}

/**
 * Content shown inside an empty Select dropdown. Renders the `emptyAction` CTA
 * (creation button or navigation link) when present and permitted, otherwise a
 * muted "no options" message. The CTA closes the dropdown on activation.
 *
 * The CTA is keyboard-reachable (it is a native button/anchor, auto-focusable
 * inside the portalled panel) and inherits the panel's opacity-only entrance, so
 * it respects `prefers-reduced-motion` via the global guard like the rest of the UI.
 */
export function SelectEmptyContent(props: { action?: SelectEmptyAction; label?: string }) {
  const ctaClass = cn(
    "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm font-medium outline-none",
    "text-popover-foreground transition-colors cursor-pointer",
    "hover:bg-accent/10 hover:text-foreground",
    "focus-visible:bg-accent/10 focus-visible:text-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
  );

  const ctaIcon = () => props.action?.icon ?? <Plus class="h-4 w-4 shrink-0 text-accent" />;

  return (
    <Show
      when={props.action && props.action.can !== false ? props.action : undefined}
      fallback={
        <div class="px-2.5 py-3 text-center text-sm text-muted-foreground">
          {/* Copy lives at the call site so this primitive stays i18n-clean; the
              neutral em-dash is the last-resort placeholder when no label is given. */}
          {props.label ?? "—"}
        </div>
      }
    >
      {(action) => (
        <div class="space-y-1">
          <Show when={action().description}>
            <p class="px-2.5 pt-1 text-xs text-muted-foreground">{action().description}</p>
          </Show>
          <Show
            when={action().href}
            fallback={
              <button
                type="button"
                class={ctaClass}
                onClick={() => {
                  action().onSelect?.();
                }}
              >
                {ctaIcon()}
                <span class="truncate">{action().label}</span>
              </button>
            }
          >
            <a href={action().href} class={ctaClass}>
              {ctaIcon()}
              <span class="truncate">{action().label}</span>
            </a>
          </Show>
        </div>
      )}
    </Show>
  );
}
